import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarClock, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';
import { formatIsoDayFr, ISO_DAY, parseFrDay } from '../../lib/dateInput';
import {
  DEFAULT_FICHE_DUE_TIME,
  HOUR_WHEEL,
  MINUTE_QUARTERS,
  formatFicheDueLabel,
  joinDueTime,
  parseFicheDueInput,
  snapToQuarter,
  splitDueTime,
} from '../../lib/tasks/taskDueTime';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

const WEEKDAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const WHEEL_ITEM_H = 32;
const WHEEL_VISIBLE = 7;

function isoToDate(iso: string): Date | null {
  if (!ISO_DAY.test(iso)) return null;
  const parts = iso.split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  return new Date(y, m - 1, d);
}

function toIsoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function labelFrom(iso: string, showTime: boolean, time: string | undefined): string {
  if (showTime) return formatFicheDueLabel(iso, time || DEFAULT_FICHE_DUE_TIME);
  return formatIsoDayFr(iso);
}

function TimeWheel({
  items,
  value,
  onChange,
  'aria-label': ariaLabel,
}: {
  items: { value: number; label: string }[];
  value: number;
  onChange: (value: number) => void;
  'aria-label': string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const itemsRef = useRef(items);
  const pad = ((WHEEL_VISIBLE - 1) / 2) * WHEEL_ITEM_H;
  valueRef.current = value;
  onChangeRef.current = onChange;
  itemsRef.current = items;

  function indexOf(v: number): number {
    const idx = itemsRef.current.findIndex((i) => i.value === v);
    return idx < 0 ? 0 : idx;
  }

  function scrollTo(v: number) {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = indexOf(v) * WHEEL_ITEM_H;
  }

  function step(dir: 1 | -1) {
    const list = itemsRef.current;
    const idx = indexOf(valueRef.current);
    const next = list[idx + dir];
    if (next) onChangeRef.current(next.value);
  }

  useLayoutEffect(() => {
    scrollTo(value);
    const t0 = window.setTimeout(() => scrollTo(value), 0);
    const t1 = window.setTimeout(() => scrollTo(value), 200);
    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
    };
  }, [value, items]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.deltaY === 0 && e.deltaX === 0) return;
      step(e.deltaY > 0 || e.deltaX > 0 ? 1 : -1);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  return (
    <div
      ref={rootRef}
      className="relative w-11"
      style={{ height: WHEEL_ITEM_H * WHEEL_VISIBLE }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-1/2 z-10 h-8 -translate-y-1/2 rounded-cime bg-cime-primary/10 ring-1 ring-cime-primary/25" />
      <div
        ref={ref}
        role="listbox"
        aria-label={ariaLabel}
        tabIndex={0}
        className="h-full overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{
          paddingTop: pad,
          paddingBottom: pad,
          WebkitMaskImage:
            'linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%)',
          maskImage:
            'linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%)',
        }}
      >
        {items.map((item) => (
          <button
            key={item.value}
            type="button"
            tabIndex={-1}
            role="option"
            aria-selected={item.value === value}
            onClick={() => onChange(item.value)}
            className={cn(
              'flex h-8 w-full items-center justify-center text-sm tabular-nums',
              item.value === value
                ? 'font-semibold text-cime-primary'
                : 'text-cime-text-muted',
            )}
            style={{ height: WHEEL_ITEM_H }}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

interface Props {
  value: string;
  onChange: (iso: string) => void;
  label?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  showTime?: boolean;
  time?: string;
  onTimeChange?: (hhmm: string) => void;
  /** Closed state drawn by the caller. The calendar popover stays the same. */
  trigger?: ReactNode;
  triggerClassName?: string;
  /** Wheels only. The day grid stays hidden. */
  timeOnly?: boolean;
  /** Opens the calendar as soon as the picker mounts (menus). */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function DatePicker({
  value,
  onChange,
  label,
  required,
  error,
  disabled,
  id,
  'aria-label': ariaLabel,
  showTime = false,
  time,
  onTimeChange,
  trigger,
  triggerClassName,
  timeOnly = false,
  defaultOpen = false,
  onOpenChange,
}: Props) {
  const inputId = id ?? (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const [open, setOpenState] = useState(defaultOpen);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const [text, setText] = useState(() => labelFrom(value, showTime, time));
  const selected = isoToDate(value);
  const [cursor, setCursor] = useState(() => selected ?? new Date());
  const [timeDraft, setTimeDraft] = useState(
    snapToQuarter(time || (showTime ? DEFAULT_FICHE_DUE_TIME : DEFAULT_FICHE_DUE_TIME)),
  );

  useEffect(() => {
    setText(labelFrom(value, showTime, time));
    const d = isoToDate(value);
    if (d) setCursor(d);
  }, [value, showTime, time]);

  useEffect(() => {
    if (showTime) setTimeDraft(snapToQuarter(time || DEFAULT_FICHE_DUE_TIME));
  }, [time, showTime]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  const hourItems = useMemo(
    () => HOUR_WHEEL.map((h) => ({ value: h, label: String(h) })),
    [],
  );
  const minuteItems = useMemo(
    () => MINUTE_QUARTERS.map((m) => ({
      value: m,
      label: String(m).padStart(2, '0'),
    })),
    [],
  );

  const { hour, minute } = splitDueTime(timeDraft);

  function commitTime(raw: string) {
    const next = snapToQuarter(raw || DEFAULT_FICHE_DUE_TIME);
    setTimeDraft(next);
    onTimeChange?.(next);
    if (value) setText(formatFicheDueLabel(value, next));
  }

  function commitText(raw: string) {
    if (raw.trim() === '') {
      if (showTime || !required) {
        onChange('');
        if (showTime) onTimeChange?.('');
        setText('');
        return;
      }
      setText(labelFrom(value, showTime, time));
      return;
    }
    if (showTime) {
      const parsed = parseFicheDueInput(raw);
      if (parsed) {
        onChange(parsed.day);
        onTimeChange?.(parsed.time);
        setTimeDraft(parsed.time);
        setText(formatFicheDueLabel(parsed.day, parsed.time));
        return;
      }
      setText(labelFrom(value, showTime, time));
      return;
    }
    const iso = parseFrDay(raw);
    if (iso) {
      onChange(iso);
      setText(formatIsoDayFr(iso));
      return;
    }
    setText(formatIsoDayFr(value));
  }

  function pick(d: Date) {
    const iso = toIsoDay(d);
    onChange(iso);
    if (showTime) {
      const next = snapToQuarter(timeDraft || time || DEFAULT_FICHE_DUE_TIME);
      onTimeChange?.(next);
      setTimeDraft(next);
      setText(formatFicheDueLabel(iso, next));
      return;
    }
    setOpen(false);
  }

  function clearDue() {
    onChange('');
    onTimeChange?.('');
    setText('');
    setOpen(false);
  }

  const panel = (
    <>
      <div className="flex items-stretch gap-3">
        {timeOnly ? null : (
          <div className="w-[252px]">
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                className="p-1 rounded hover:bg-neutral-100 text-neutral-600"
                aria-label="Mois précédent"
                onClick={() => setCursor((c) => addMonths(c, -1))}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <p className="text-sm font-medium text-cime-text-primary capitalize">
                {format(cursor, 'LLLL yyyy', { locale: fr })}
              </p>
              <button
                type="button"
                className="p-1 rounded hover:bg-neutral-100 text-neutral-600"
                aria-label="Mois suivant"
                onClick={() => setCursor((c) => addMonths(c, 1))}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-0.5 text-center">
              {WEEKDAYS.map((d, i) => (
                <span key={`${d}-${i}`} className="text-[10px] font-semibold uppercase text-cime-text-muted py-1">
                  {d}
                </span>
              ))}
              {days.map((d) => {
                const inMonth = isSameMonth(d, cursor);
                const isSelected = selected ? isSameDay(d, selected) : false;
                const isToday = isSameDay(d, new Date());
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    onClick={() => pick(d)}
                    className={cn(
                      'h-8 text-xs rounded-cime',
                      inMonth ? 'text-cime-text-primary' : 'text-neutral-300',
                      isSelected && 'bg-cime-primary text-white',
                      !isSelected && inMonth && 'hover:bg-cime-primary/10',
                      isToday && !isSelected && 'ring-1 ring-cime-gold',
                    )}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {showTime ? (
          <div className={cn('flex flex-col items-center', !timeOnly && 'border-l border-cime-border pl-3')}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-cime-text-muted mb-1">
              Heure
            </p>
            <div className="flex items-center gap-0.5">
              <TimeWheel
                items={hourItems}
                value={hour}
                onChange={(h) => commitTime(joinDueTime(h, minute))}
                aria-label="Heure"
              />
              <span className="text-sm font-semibold text-cime-text-muted pb-0.5">:</span>
              <TimeWheel
                items={minuteItems}
                value={minute}
                onChange={(m) => commitTime(joinDueTime(hour, m))}
                aria-label="Minutes"
              />
            </div>
          </div>
        ) : null}
      </div>
      {value && !timeOnly && (showTime || !required) ? (
        <button
          type="button"
          onClick={clearDue}
          className="mt-2 text-xs font-medium text-cime-urgent hover:underline"
        >
          {showTime ? "Retirer l'échéance" : 'Retirer la date'}
        </button>
      ) : null}
    </>
  );

  return (
    <div className={trigger ? 'block w-full' : 'flex flex-col gap-1'}>
      {label && !trigger ? (
        <label
          htmlFor={inputId}
          className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide"
        >
          {label}
          {required ? <span className="text-cime-urgent"> *</span> : null}
        </label>
      ) : null}
      <Popover open={open} onOpenChange={(next) => { if (!disabled) setOpen(next); }}>
        {trigger ? (
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={ariaLabel ?? label}
              className={cn(
                'block w-full border-0 bg-transparent p-0 text-left disabled:opacity-50',
                triggerClassName,
              )}
            >
              {trigger}
            </button>
          </PopoverTrigger>
        ) : (
          <div className="relative">
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                className="absolute left-1.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-cime text-neutral-400 hover:text-cime-primary hover:bg-neutral-100"
                aria-label="Ouvrir le calendrier"
              >
                <CalendarClock className="w-4 h-4 mx-auto" />
              </button>
            </PopoverTrigger>
            <input
              id={inputId}
              type="text"
              inputMode={showTime ? 'text' : 'numeric'}
              autoComplete="off"
              placeholder={showTime ? 'jj/mm/aaaa · 09:00' : 'jj/mm/aaaa'}
              required={required}
              disabled={disabled}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onBlur={() => commitText(text)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitText(text);
                }
              }}
              aria-label={ariaLabel ?? label}
              aria-invalid={!!error}
              className={cn(
                'w-full h-10 pl-9 pr-3 text-sm rounded-cime border bg-white',
                'placeholder:text-neutral-400 text-neutral-900',
                'focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent',
                error
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-neutral-300 hover:border-neutral-400',
              )}
            />
          </div>
        )}
        <PopoverContent
          align="start"
          className="z-[80] w-auto rounded-cime border-cime-border bg-white p-3 shadow-lg"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {panel}
        </PopoverContent>
      </Popover>
      {error ? (
        <p className="text-xs text-red-600" role="alert">{error}</p>
      ) : null}
    </div>
  );
}
